import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { callSheetApi } from "@/lib/api";
import {
  ClipboardList,
  CalendarDays,
  MapPin,
  Users,
  Clock,
  CheckCircle2,
  ArrowRight,
  Film,
} from "lucide-react";
import { toast } from "sonner";

export default function CallSheetsListPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        setItems(await callSheetApi.listAll());
      } catch (e) {
        toast.error("Failed to load call sheets");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div className="min-h-screen max-w-screen-2xl mx-auto px-6 lg:px-10 py-10">
      <div className="flex items-center gap-3 mb-2">
        <ClipboardList className="w-5 h-5 text-amber-500" />
        <div className="text-overline text-zinc-500">Production</div>
      </div>
      <h1 className="font-heading text-4xl sm:text-5xl font-light text-zinc-100 mb-2">
        Call Sheets
      </h1>
      <p className="text-zinc-500 text-sm mt-2 max-w-lg">
        Every project&apos;s call sheet in one place. Click any to edit, download
        the PDF, email, or share a mobile link.
      </p>

      <div className="mt-10">
        {loading ? (
          <div className="text-zinc-500">Loading…</div>
        ) : items.length === 0 ? (
          <div className="border border-dashed border-zinc-800 rounded-md p-12 text-center">
            <ClipboardList
              className="w-7 h-7 text-amber-500 mx-auto mb-3"
              strokeWidth={1.5}
            />
            <h3 className="font-heading text-xl text-zinc-100 mb-1">
              No call sheets yet
            </h3>
            <p className="text-zinc-500 text-sm">
              Open any project and click <strong className="text-amber-500">Call Sheet</strong> to create one.
            </p>
          </div>
        ) : (
          <div className="border border-zinc-800 rounded-md overflow-hidden bg-zinc-900">
            {items.map((cs, i) => (
              <Link
                key={cs.id}
                to={`/projects/${cs.project_id}/call-sheet?day=${cs.id}`}
                data-testid={`call-sheet-row-${cs.id}`}
                className={`group grid grid-cols-12 gap-4 items-center px-5 py-4 ${
                  i < items.length - 1 ? "border-b border-zinc-800/60" : ""
                } hover:bg-zinc-800/30 transition-colors`}
              >
                <div className="col-span-12 md:col-span-4 min-w-0">
                  <div className="flex items-center gap-3">
                    <div
                      className={`w-1 h-10 rounded-sm shrink-0 ${
                        cs.cover_color === "rose"
                          ? "bg-rose-500"
                          : cs.cover_color === "teal"
                            ? "bg-teal-500"
                            : cs.cover_color === "violet"
                              ? "bg-violet-500"
                              : cs.cover_color === "blue"
                                ? "bg-blue-500"
                                : "bg-amber-500"
                      }`}
                    />
                    <div className="min-w-0">
                      <div className="font-heading text-base text-zinc-100 truncate group-hover:text-amber-500 transition-colors flex items-center gap-2">
                        <span className="truncate">{cs.project_name}</span>
                        {cs.total_days > 1 && (
                          <span
                            className="text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded-sm bg-amber-500/10 text-amber-400 border border-amber-500/30 shrink-0"
                            title={`${cs.total_days} days in this project`}
                          >
                            {cs.day_label?.trim() || `Day ${cs.day_number}`}/{cs.total_days}
                          </span>
                        )}
                      </div>
                      {cs.day_of && (
                        <div className="text-overline text-zinc-500 mt-0.5">
                          {cs.day_of}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="col-span-6 md:col-span-2 text-sm">
                  <div className="text-overline text-zinc-600 mb-0.5">Date</div>
                  <div className="text-zinc-300 flex items-center gap-1.5">
                    <CalendarDays className="w-3.5 h-3.5 text-zinc-500" />
                    {fmtDate(cs.shoot_date) || (
                      <span className="text-zinc-600 italic">Unscheduled</span>
                    )}
                  </div>
                </div>

                <div className="col-span-6 md:col-span-2 text-sm">
                  <div className="text-overline text-zinc-600 mb-0.5">Call</div>
                  <div className="text-zinc-300 flex items-center gap-1.5">
                    <Clock className="w-3.5 h-3.5 text-zinc-500" />
                    {cs.general_call || <span className="text-zinc-600">—</span>}
                  </div>
                </div>

                <div className="col-span-8 md:col-span-2 flex items-center gap-4 text-xs text-zinc-400">
                  <Stat icon={MapPin} value={cs.location_count} />
                  <Stat icon={Film} value={cs.talent_count} />
                  <Stat icon={Users} value={cs.crew_count} />
                </div>

                <div className="col-span-4 md:col-span-2 flex items-center justify-end gap-3">
                  {cs.confirmation_total > 0 && (
                    <div
                      className="flex items-center gap-1 text-xs"
                      title={`${cs.confirmation_confirmed} of ${cs.confirmation_total} confirmed`}
                    >
                      <CheckCircle2
                        className={`w-3.5 h-3.5 ${
                          cs.confirmation_confirmed === cs.confirmation_total
                            ? "text-emerald-400"
                            : "text-amber-500"
                        }`}
                      />
                      <span className="font-mono">
                        {cs.confirmation_confirmed}/{cs.confirmation_total}
                      </span>
                    </div>
                  )}
                  <ArrowRight className="w-4 h-4 text-zinc-600 group-hover:text-amber-500 transition-colors" />
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ icon: Icon, value }) {
  return (
    <span className="inline-flex items-center gap-1">
      <Icon className="w-3 h-3" />
      <span className="font-mono">{value}</span>
    </span>
  );
}

function fmtDate(iso) {
  if (!iso) return "";
  try {
    const [y, m, d] = iso.split("-").map(Number);
    return new Date(y, (m || 1) - 1, d || 1).toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}
